<script setup lang="ts">
const { userRole } = useAuth()

const { data } = useWorkspaceSettings()
const hasUnhandledThreats = computed(() => {
  return Boolean(data.value?.unhandled_threats_count)
})

const route = useRoute()
const integrationsIsActive = computed(() => {
  return route.path.startsWith("/settings/integrations")
})

const { data: workspace } = useWorkspaceSettings()

const { $trackEvent } = useNuxtApp()

function trackKnowledgeBaseClick() {
  $trackEvent("knowledge_base_link_clicked", { route: route.fullPath })
}
</script>
<template>
  <div data-slot="section" class="flex flex-col gap-0.5">
    <SidebarNavigationItem to="/dashboard">
      <SvgoPikaDashboard />
      <SidebarNavigationItemText>
        {{ $t("navigation.dashboard") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>

    <SidebarNavigationExpandableItem
      v-if="
        $hasFeature('admin:human-risk-intelligence') &&
        workspace?.human_risk_intelligence_settings.is_enabled
      "
      to="/human-risk-intelligence/activity"
      active-prefix="/human-risk-intelligence"
    >
      <template #icon>
        <SvgoPikaLayers3 />
      </template>

      <SidebarNavigationItemText>
        {{ $t("navigation.human_risk_intelligence") }}
      </SidebarNavigationItemText>

      <template #content>
        <SidebarNavigationExpandableContent>
          <SidebarNavigationSubItem to="/human-risk-intelligence/activity">
            {{ $t("human_risk_intelligence.navigation.overview") }}
          </SidebarNavigationSubItem>

          <SidebarNavigationSubItem
            to="/human-risk-intelligence/risk-framework"
          >
            {{ $t("human_risk_intelligence.navigation.risk_framework") }}
          </SidebarNavigationSubItem>

          <SidebarNavigationSubItem to="/human-risk-intelligence/osint">
            {{ $t("human_risk_intelligence.navigation.osint") }}
          </SidebarNavigationSubItem>
        </SidebarNavigationExpandableContent>
      </template>
    </SidebarNavigationExpandableItem>
    <SidebarNavigationItemUpsellOverlay
      v-else
      feature="human-risk-intelligence"
    >
      <SidebarNavigationItem to="/human-risk-intelligence/activity">
        <SvgoPikaLayers3 />
        <SidebarNavigationItemText>
          {{ $t("navigation.human_risk_intelligence") }}
        </SidebarNavigationItemText>
      </SidebarNavigationItem>
    </SidebarNavigationItemUpsellOverlay>

    <SidebarNavigationItem
      v-if="$hasFeature('admin:simulations')"
      to="/simulations"
    >
      <SvgoPikaSimulation />
      <SidebarNavigationItemText>
        {{ $t("navigation.simulations.index") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
    <SidebarNavigationItem
      v-if="
        $hasFeature('admin:training') ||
        $hasFeature('admin:training:standard-training') ||
        $hasFeature('admin:training:advanced-training')
      "
      to="/elearning"
    >
      <SvgoPikaTraining />
      <SidebarNavigationItemText>
        {{ $t("navigation.training") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
    <SidebarNavigationItemUpsellOverlay feature="policy-management">
      <SidebarNavigationItem to="/policies">
        <SvgoPikaFileLightning />
        <SidebarNavigationItemText>
          {{ $t("navigation.policies") }}
        </SidebarNavigationItemText>
      </SidebarNavigationItem>
    </SidebarNavigationItemUpsellOverlay>

    <SidebarNavigationItem
      v-if="$hasFeature('admin:automations')"
      to="/automation"
    >
      <SvgoPikaAutomation />
      <SidebarNavigationItemText>
        {{ $t("navigation.automation.index") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
    <SidebarNavigationItemUpsellOverlay feature="threat-management">
      <SidebarNavigationItem to="/threats" :show-badge="hasUnhandledThreats">
        <SvgoPikaThreat />
        <SidebarNavigationItemText>
          {{ $t("navigation.threats") }}
        </SidebarNavigationItemText>
      </SidebarNavigationItem>
    </SidebarNavigationItemUpsellOverlay>
    <SidebarNavigationItem
      v-if="
        $hasFeature('admin:breaches') ||
        $hasFeature('admin:security:data-breach-monitoring')
      "
      to="/breaches"
    >
      <SvgoPikaBreach />
      <SidebarNavigationItemText>
        {{ $t("navigation.breaches") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
  </div>
  <div
    data-slot="section"
    class="flex flex-col gap-0.5 @[12rem]/navigation:mt-8"
  >
    <h3
      class="mb-1 hidden px-2 text-xs/6 font-medium text-zinc-500 @[12rem]/navigation:inline-block"
    >
      {{ $t("navigation.configuration") }}
    </h3>
    <SidebarNavigationItem to="/team">
      <SvgoPikaUser />
      <SidebarNavigationItemText>
        {{ $t("navigation.team") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
    <SidebarNavigationItem
      v-if="userRole == 'admin'"
      to="/settings/integrations"
    >
      <SvgoPikaIntegration />
      <SidebarNavigationItemText>
        {{ $t("navigation.integrations") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
    <SidebarNavigationItem
      v-if="userRole == 'admin'"
      to="/settings"
      :disable-active-indicator="integrationsIsActive"
    >
      <SvgoPikaSettings />
      <SidebarNavigationItemText>
        {{ $t("navigation.settings.index") }}
      </SidebarNavigationItemText>
    </SidebarNavigationItem>
  </div>
  <div aria-hidden="true" class="mt-8 flex-1" />

  <SidebarNavigationItem
    target="_blank"
    to="https://help.moxso.com?referrer=admin"
    @click="trackKnowledgeBaseClick"
  >
    <SvgoPikaBulbOff class="size-4" />
    <SidebarNavigationItemText>
      {{ $t("navigation.knowledge_base") }}
    </SidebarNavigationItemText>
  </SidebarNavigationItem>
</template>
